/**
 * Aperçu client (non autoritaire) d'un CSV d'inscrits avant import : lignes détectées,
 * doublons d'email, colonnes obligatoires manquantes. Le back reste seul juge à l'import réel.
 */

/** Reproduit la validation par ligne du back (AttendeeCsvImportServiceImpl.parseRow). */
export type AttendeesCsvRowError = 'missingFirstName' | 'missingEmail' | 'invalidEmail';

export interface AttendeesCsvRow {
  line: number;
  firstName: string;
  lastName: string | null;
  email: string;
  phone: string | null;
  isDuplicateEmail: boolean;
  error: AttendeesCsvRowError | null;
}

export interface AttendeesCsvDuplicate {
  email: string;
  lines: number[];
}

/** Colonne présente dans le fichier mais non reconnue à cause d'un accent (ex. "Téléphone"). */
export interface AttendeesCsvHeaderWarning {
  header: string;
  suggestion: string;
}

export interface AttendeesCsvPreview {
  rowCount: number;
  rows: AttendeesCsvRow[];
  duplicates: AttendeesCsvDuplicate[];
  invalidRows: AttendeesCsvRow[];
  headerWarnings: AttendeesCsvHeaderWarning[];
  missingFirstName: boolean;
  missingEmail: boolean;
  unreadable: boolean;
}

// Même regex que le back (HTTP.ERROR.400.ATTENDEE.CSV.EMAIL.INVALID).
const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeHeaderCell = (value: string): string =>
  value
    .replace(/﻿/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-zà-ÿ]/g, '');

// Version tolérante (accents retirés) : sert uniquement à repérer un en-tête "presque bon"
// (ex. "Téléphone") que la validation stricte, elle, ignore silencieusement.
const stripDiacritics = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '');

const FIRST_NAME_KEYS = ['firstname', 'prenom'];
const LAST_NAME_KEYS = ['lastname', 'nom'];
const EMAIL_KEYS = ['email'];
const PHONE_KEYS = ['phonenumber', 'telephone', 'phone'];

const HEADER_SUGGESTIONS: Record<string, string> = {
  firstname: 'FirstName',
  prenom: 'Prenom',
  lastname: 'LastName',
  nom: 'Nom',
  email: 'Email',
  phonenumber: 'PhoneNumber',
  telephone: 'Telephone',
  phone: 'Phone',
};

const splitRecords = (text: string): string[] => {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const records: string[] = [];
  let current = '';
  let insideQuotes = false;
  let atColumnStart = true;

  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i];
    if (char === '"' && (insideQuotes || atColumnStart)) {
      current += char;
      if (insideQuotes && normalized[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
      atColumnStart = false;
    } else if (char === '\n' && !insideQuotes) {
      records.push(current);
      current = '';
      atColumnStart = true;
    } else {
      current += char;
      atColumnStart = !insideQuotes && (char === ',' || char === ';');
    }
  }
  if (current.trim().length > 0) {
    records.push(current);
  }
  return records;
};

const detectSeparator = (record: string): string => (record.includes(';') ? ';' : ',');

const splitColumns = (record: string): string[] => {
  const separator = detectSeparator(record);
  const columns: string[] = [];
  let value = '';
  let insideQuotes = false;
  let atColumnStart = true;

  for (let i = 0; i < record.length; i++) {
    const char = record[i];
    if (char === '"' && (insideQuotes || atColumnStart)) {
      if (insideQuotes && record[i + 1] === '"') {
        value += '"';
        i++;
      } else {
        insideQuotes = !insideQuotes;
      }
      atColumnStart = false;
    } else if (char === separator && !insideQuotes) {
      columns.push(value);
      value = '';
      atColumnStart = true;
    } else {
      value += char;
      atColumnStart = false;
    }
  }
  columns.push(value);
  return columns;
};

const EMPTY_UNREADABLE_PREVIEW: AttendeesCsvPreview = {
  rowCount: 0,
  rows: [],
  duplicates: [],
  invalidRows: [],
  headerWarnings: [],
  missingFirstName: true,
  missingEmail: true,
  unreadable: true,
};

const ALL_HEADER_KEYS = [...FIRST_NAME_KEYS, ...LAST_NAME_KEYS, ...EMAIL_KEYS, ...PHONE_KEYS];

export function parseAttendeesCsvText(text: string): AttendeesCsvPreview {
  const records = splitRecords(text);
  if (records.length === 0) {
    return EMPTY_UNREADABLE_PREVIEW;
  }

  const rawHeaderColumns = splitColumns(records[0]);
  const headerColumns = rawHeaderColumns.map(normalizeHeaderCell);

  const firstNameIndex = headerColumns.findIndex((c) => FIRST_NAME_KEYS.includes(c));
  const lastNameIndex = headerColumns.findIndex((c) => LAST_NAME_KEYS.includes(c));
  const emailIndex = headerColumns.findIndex((c) => EMAIL_KEYS.includes(c));
  const phoneIndex = headerColumns.findIndex((c) => PHONE_KEYS.includes(c));

  const missingFirstName = firstNameIndex === -1;
  const missingEmail = emailIndex === -1;

  // En-tête présent mais ignoré à cause d'un accent (ex. "Téléphone" au lieu de "Telephone") :
  // silencieux sinon, alors que la colonne est simplement traitée comme absente (données perdues).
  const headerWarnings: AttendeesCsvHeaderWarning[] = [];
  headerColumns.forEach((normalized, index) => {
    if (ALL_HEADER_KEYS.includes(normalized)) return;
    const lenient = stripDiacritics(normalized);
    if (lenient !== normalized && ALL_HEADER_KEYS.includes(lenient)) {
      const rawHeader = rawHeaderColumns[index]?.trim();
      if (rawHeader) {
        headerWarnings.push({ header: rawHeader, suggestion: HEADER_SUGGESTIONS[lenient] });
      }
    }
  });

  const rawRows: Array<{
    line: number;
    firstName: string;
    lastName: string | null;
    email: string;
    phone: string | null;
  }> = [];
  const emailLineMap = new Map<string, number[]>();

  for (let i = 1; i < records.length; i++) {
    const record = records[i];
    if (record.trim() === '') continue;

    const columns = splitColumns(record);
    const lineNumber = i + 1; // 1-indexé, en-tête = ligne 1
    const email = (emailIndex >= 0 ? columns[emailIndex] : '')?.trim() ?? '';
    const firstName = (firstNameIndex >= 0 ? columns[firstNameIndex] : '')?.trim() ?? '';
    const lastName = lastNameIndex >= 0 ? (columns[lastNameIndex]?.trim() ?? '') || null : null;
    const phone = phoneIndex >= 0 ? (columns[phoneIndex]?.trim() ?? '') || null : null;

    rawRows.push({ line: lineNumber, firstName, lastName, email, phone });

    const normalizedEmail = email.toLowerCase();
    if (normalizedEmail) {
      const lines = emailLineMap.get(normalizedEmail) ?? [];
      lines.push(lineNumber);
      emailLineMap.set(normalizedEmail, lines);
    }
  }

  const duplicateEmails = new Set(
    Array.from(emailLineMap.entries())
      .filter(([, lines]) => lines.length > 1)
      .map(([email]) => email),
  );

  const rows: AttendeesCsvRow[] = rawRows.map((row) => {
    let error: AttendeesCsvRowError | null = null;
    // Mêmes règles que le back : prénom et email obligatoires par ligne, email au bon format.
    if (!missingFirstName && !row.firstName) {
      error = 'missingFirstName';
    } else if (!missingEmail && !row.email) {
      error = 'missingEmail';
    } else if (!missingEmail && row.email && !EMAIL_FORMAT_REGEX.test(row.email)) {
      error = 'invalidEmail';
    }

    return {
      ...row,
      isDuplicateEmail: row.email ? duplicateEmails.has(row.email.toLowerCase()) : false,
      error,
    };
  });

  const duplicates: AttendeesCsvDuplicate[] = Array.from(emailLineMap.entries())
    .filter(([, lines]) => lines.length > 1)
    .map(([email, lines]) => ({ email, lines }));

  const invalidRows = rows.filter((row) => row.error !== null);

  return {
    rowCount: rawRows.length,
    rows,
    duplicates,
    invalidRows,
    headerWarnings,
    missingFirstName,
    missingEmail,
    unreadable: false,
  };
}

export interface AttendeesCsvImportLimit {
  remainingSlots: number;
  refusedRowCount: number;
}

/** Lignes qui partiront au back : valides, dédoublonnées sur l'email (comme parse()). */
const countCandidateRows = (preview: AttendeesCsvPreview): number =>
  new Set(preview.rows.filter((row) => row.error === null).map((row) => row.email.toLowerCase()))
    .size;

/**
 * Estime la troncature que le back appliquera à un compte gratuit, pour l'annoncer avant l'import.
 * Majorant du refus : le back ne consomme pas de place pour un email déjà inscrit au webinaire,
 * information que le front n'a pas. Renvoie null quand rien ne serait refusé.
 */
export function resolveAttendeesCsvImportLimit(
  preview: AttendeesCsvPreview,
  registeredAttendeesCount: number | null | undefined,
  limit: number,
): AttendeesCsvImportLimit | null {
  if (registeredAttendeesCount == null) return null;

  const remainingSlots = Math.max(0, limit - registeredAttendeesCount);
  const refusedRowCount = countCandidateRows(preview) - remainingSlots;

  return refusedRowCount > 0 ? { remainingSlots, refusedRowCount } : null;
}

export async function buildAttendeesCsvPreview(file: File): Promise<AttendeesCsvPreview> {
  let text: string;
  try {
    text = await file.text();
  } catch {
    return EMPTY_UNREADABLE_PREVIEW;
  }

  return parseAttendeesCsvText(text);
}
